import { getBaseUrl } from "@/api/base.url";
import {
  authControllerLogin,
  authControllerLogout,
  authControllerRegistration,
  authControllerSendNewActivateLink,
} from "@/api/types/main";
import { AuthResponseDto, RegistrationBodyDto, UserInfoDto } from "@/api/types/main/schemas";

import axios from "axios";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface UserStore {
  user: UserInfoDto;
  isLoading: boolean;
  isAuth: boolean | undefined;
  accessToken: string | undefined;
  setLoading: (isLoading: boolean) => void;
  setAuth: (isAuth: boolean) => void;
  setUser: (user: UserInfoDto) => void;
  login: (email: string, password: string) => Promise<void>;
  registration: (user: RegistrationBodyDto) => Promise<void>;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
  sendNewActivationLink: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  setAccessToken: (accessToken: string | undefined) => void;
  getAccessToken: () => string | undefined;
}

type PersistedState = Pick<UserStore, "accessToken">;

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      user: {} as UserInfoDto,
      isLoading: false,
      isAuth: undefined,
      accessToken: undefined,
      getAccessToken: () => get().accessToken,
      setAccessToken: (accessToken: string | undefined) =>
        set((state) => ({ ...state, accessToken })),
      setLoading: (isLoading: boolean) => set((state) => ({ ...state, isLoading })),
      setAuth: (isAuth: boolean) => set((state) => ({ ...state, isAuth })),
      setUser: (user: UserInfoDto) => set((state) => ({ ...state, user })),

      registration: async (user: RegistrationBodyDto) => {
        const response = await authControllerRegistration(user);
        set({
          isAuth: true,
          user: response.user,
          accessToken: response.access_token,
        });
      },

      refreshAccessToken: async () => {
        try {
          const response = await axios.get<AuthResponseDto>(`${getBaseUrl()}/api/auth/refresh`, {
            withCredentials: true,
          });
          if (!response.data.access_token) {
            throw new Error();
          }
          set({
            isAuth: true,
            user: response.data.user,
            accessToken: response.data.access_token,
          });
        } catch (e) {
          console.log("Ошибка обновления токена", e);
          set({
            isAuth: false,
            user: {} as UserInfoDto,
            accessToken: undefined,
          });
          throw e; // Пробрасываем ошибку дальше для правильной обработки в api.instance
        }
      },

      login: async (email: string, password: string) => {
        const response = await authControllerLogin({ email, password });
        set({
          isAuth: true,
          user: response.user,
          accessToken: response.access_token,
        });
      },

      logout: async () => {
        try {
          await authControllerLogout();
          set({
            isAuth: false,
            user: {} as UserInfoDto,
            accessToken: undefined,
          });
        } catch (e) {
          console.log(e);
        }
      },

      sendNewActivationLink: async () => {
        try {
          await authControllerSendNewActivateLink();
        } catch (error) {
          console.log(error);
        }
      },

      checkAuth: async () => {
        if (get().isLoading) return;

        set({ isLoading: true });
        try {
          await get().refreshAccessToken();
        } catch (e) {
          console.log("Ошибка проверки авторизации", e);
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "user-storage",
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state: UserStore): PersistedState => ({
        accessToken: state.accessToken,
      }),
      migrate: (persistedState, version: number) => {
        const typedState = persistedState as PersistedState;
        if (version === 0) {
          return {
            accessToken: typedState.accessToken,
          };
        }
        return persistedState;
      },
    }
  )
);
